import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import fordStyles from "./FieldView-Ford.module.scss";
import lincolnStyles from "./FieldView-Lincoln.module.scss";
import { withTranslation } from "react-i18next";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import DealerGroupingClient from "../../clients/DealerGroupingClient";
import {
  IS_CANADA_MARKET,
  IS_FIELD_VIEW_USER,
  IS_USA_MARKET,
  IS_FORD_BRAND,
} from "../../utils/EmpUtil";
import DealerSearchResults from "./DealerSearchResults";
import {
  Box,
  Grid,
  RadioGroup,
  Radio,
  FormControlLabel,
  Typography,
} from "@mui/material";
import CustomerReservationsClient from "../../clients/CustomerReservationsClient";
import { LANGUAGE_MARKET_MAP } from "../../common/Constants";
import FieldViewOrderIdSearchResultsTable from "../search/FieldViewOrderIdSearchResultsTable";
import LocalizedInfoClient from "../../clients/LocalizedInfoClient";
import UpperCaseText from "../../utils/UpperCaseText";
import SharedToolTip from "../../shared/sharedToolTip/SharedToolTip";
import EMPButton from "../../shared/components/EMPButton/EMPButton";

const DealerSearch = (props) => {
  const [state, setState] = useState({
    code: "",
    orderId: "",
    invalidCodeError: false,
    dealerNotFoundError: false,
    dealers: {},
    showDealerResults: false,
    showOrderResults: false,
    showOrderDetails: false,
    searchMethod: "searchBySalesCodePA",
  });

  const isNADealerRef = useRef(
    props.user.market.toUpperCase() === "CAN" ||
      props.user.market.toUpperCase() === "USA"
  );

  const setStateFields = (fields) => {
    setState((prevState) => ({
      ...prevState,
      ...fields,
    }));
  };

  const isValidSearchCode = (code, market) => {
    const regexUSA = /^[FL]?\d{5}$/i;
    const regexCAN = /^C?[BA]\d{4}$/i;

    if (IS_USA_MARKET(market)) {
      return regexUSA.test(code);
    } else if (IS_CANADA_MARKET(market)) {
      return regexCAN.test(code);
    } else {
      return false;
    }
  };

  const isValidPACode = (code, market) => {
    const regexUSA = /^\d{5}$/;
    const regexCAN = /^[BA]\d{4}$/i;

    if (IS_USA_MARKET(market)) {
      return regexUSA.test(code);
    } else if (IS_CANADA_MARKET(market)) {
      return regexCAN.test(code);
    } else {
      return false;
    }
  };

  const fetchLocalizedInfo = (marketID) => {
    LocalizedInfoClient.getLocalizedInfo(props.user, marketID).then(() => {
      setStateFields({ renderApp: true });
    });
  };

  useEffect(() => {
    const defaultUserMarket = props.user.market.toUpperCase();
    isNADealerRef.current =
      props.user.market.toUpperCase() === "CAN" ||
      props.user.market.toUpperCase() === "USA";
    fetchLocalizedInfo(defaultUserMarket);
  }, []);

  const updateCurrentDealer = (dealer) => {
    props.updateCurrentDealer(dealer);
  };

  const searchDealerFromOrderId = (code, market) => {
    const isValidSearchCodeResult = isValidSearchCode(code, market);
    const isValidPACodeResult = isValidPACode(code, market);
    if (isValidSearchCodeResult) {
      DealerGroupingClient.getDealerGroup(
        props.user,
        code,
        null,
        IS_FIELD_VIEW_USER(props.user.userType),
        isValidPACodeResult && code
      )
        .then((data) => {
          const dealers = data;
          if (!dealers) {
            setStateFields({ dealerDataForOrderSearch: null });
          } else if (dealers.length > 0) {
            props.user.setDealerGroup(dealers);
            setStateFields({ dealerDataForOrderSearch: dealers[0] });
          }
        })
        .catch(() => {
          return null;
        });
    }
  };

  const parseReservations = (reservations) => {
    const tempReservations = {};
    reservations.forEach((reservation) => {
      if (reservation.dealerId in tempReservations) {
        tempReservations[reservation.dealerId].push(reservation);
      } else {
        tempReservations[reservation.dealerId] = [];
        searchDealerFromOrderId(
          reservation.dealerId.substring(10, 16),
          props.user.market
        );
        tempReservations[reservation.dealerId].push(reservation);
      }
    });

    setStateFields({
      reservations: tempReservations,
      showOrderResults: true,
    });

    props.hideOrShow(false);
  };

  const getDealerGroup = (validPACode) => {
    props.hideOrShow(true);
    DealerGroupingClient.getDealerGroup(
      props.user,
      !validPACode && state.code,
      null,
      IS_FIELD_VIEW_USER(props.user.userType),
      validPACode && state.code
    )
      .then((data) => {
        let dealers = data;
        if (!validPACode) {
          dealers = dealers.filter(
            (dealer) =>
              UpperCaseText(dealer.salesCode) === UpperCaseText(state.code)
          );
        }
        if (!dealers || dealers.length === 0) {
          setStateFields({
            dealerNotFoundError: true,
            showDealerResults: false,
          });
        } else {
          props.user.setDealerGroup(dealers);
          props.user.setLanguageCode(props.i18n.language);
          setStateFields({ dealers, showDealerResults: true });
        }
        props.hideOrShow(false);
      })
      .catch(() => {
        setStateFields({ dealerNotFoundError: true, showDealerResults: false });
      })
      .finally(() => {
        props.hideOrShow(false);
      });
  };

  const handleDealerSearch = () => {
    const code = state.code;
    const market = props.user.market;
    const isValidPAOrSalesCode = isValidSearchCode(code, market);

    if (isValidPAOrSalesCode) {
      setStateFields({
        dealers: {},
        showDealerResults: false,
        dealerNotFoundError: false,
        invalidCodeError: false,
      });
      getDealerGroup(isValidPACode(code, market));
    } else {
      setStateFields({ invalidCodeError: true, showDealerResults: false });
    }
  };

  const handleOrderSearch = () => {
    props.hideOrShow(true);
    CustomerReservationsClient.searchCustomerReservations(
      state.orderId,
      props.user,
      LANGUAGE_MARKET_MAP[props.user.market.toUpperCase()],
      null,
      props.user.market
    )
      .then((response) => {
        parseReservations(response);
      })
      .catch(() => {
        setStateFields({ reservations: {}, showOrderResults: true });
      })
      .finally(() => {
        props.hideOrShow(false);
      });
  };

  const handleInputChange = (event) => {
    if (state.searchMethod === "searchBySalesCodePA") {
      setStateFields({
        code: event.target.value.trim().toUpperCase(),
        invalidCodeError: false,
        dealerNotFoundError: false,
      });
    } else if (state.searchMethod === "searchByOrderId") {
      setStateFields({
        orderId: event.target.value.trim(),
      });
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (state.searchMethod === "searchBySalesCodePA") {
      handleDealerSearch();
    } else if (state.searchMethod === "searchByOrderId") {
      handleOrderSearch();
    }
  };

  const styles = IS_FORD_BRAND(props.user.brand) ? fordStyles : lincolnStyles;
  const subheaderText = IS_USA_MARKET(props.user.market)
    ? props.t("fieldView.subheader")
    : props.t("fieldView.subheaderForCanada");
  const salesCodePlaceholder = IS_USA_MARKET(props.user.market)
    ? props.t("fieldView.paOrSalesCodeTextInputPlaceholder")
    : props.t("fieldView.paOrSalesCodeTextInputPlaceholderForCanada");

  let salesCodeResultContent = "";
  if (state.searchMethod === "searchBySalesCodePA") {
    if (state.invalidCodeError) {
      salesCodeResultContent = (
        <div className={styles.error}>{props.t("fieldView.codeError")}</div>
      );
    } else if (state.dealerNotFoundError) {
      salesCodeResultContent = (
        <div className={styles.errorContainer}>
          <div className={styles.error}>
            {props.t("fieldView.dealerNotFoundError1")}
          </div>
          <div className={styles.error}>
            {props.t("fieldView.dealerNotFoundError2")}
          </div>
        </div>
      );
    } else if (state.showDealerResults) {
      salesCodeResultContent = (
        <Box className="pageWrapper" sx={{ pt: "24px" }}>
          <DealerSearchResults
            {...props}
            dealerBrand={props.user.dealerBrand}
            dealers={state.dealers}
            updateCurrentDealer={updateCurrentDealer}
          />
        </Box>
      );
    }
  }

  let orderSearchResultContent = "";
  if (state.searchMethod === "searchByOrderId" && state.showOrderResults) {
    if (Object.keys(state.reservations).length > 0) {
      orderSearchResultContent = Object.keys(state.reservations).map(
        (commonDealerId, index) => {
          return (
            <FieldViewOrderIdSearchResultsTable
              {...props}
              key={commonDealerId}
              isNADealer={isNADealerRef.current}
              index={index}
              fromFieldView={true}
              commonDealerId={commonDealerId}
              totalNumberOfResults={state.reservations[commonDealerId].length}
              reservations={state.reservations[commonDealerId]}
              dealerData={state.dealerDataForOrderSearch}
            />
          );
        }
      );
    } else {
      orderSearchResultContent = (
        <div id="noOrders" key="none-found" className={styles.error}>
          {props.t("CrcOrderSearch.noOrdersFoundErrorMessage")}
        </div>
      );
    }
  }

  if (!state.renderApp || state.showOrderDetails) {
    return "";
  }

  return (
    <section>
      <div data-testid="dealerSearch" className={styles.searchIndent}>
        <Typography variant="h1" sx={{ my: 4 }}>
          {props.t("fieldView.title")}
        </Typography>
        <Typography variant="body1">{subheaderText}</Typography>
        <RadioGroup
          value={state.searchMethod}
          onChange={(e) => {
            e.target.value === "searchByOrderId"
              ? setStateFields({ code: "", searchMethod: e.target.value })
              : setStateFields({
                  orderId: "",
                  searchMethod: e.target.value,
                });
          }}
        >
          <Grid
            container
            alignItems="center"
            justifyContent="flex-start"
            className={styles.searchBar}
          >
            <Grid item>
              <FormControlLabel
                value="searchBySalesCodePA"
                control={<Radio />}
                data-testid="searchBySalesCodePA"
                label={
                  <Typography variant="body2">
                    {props.t("CrcOrderSearch.searchBySalesCode")}
                  </Typography>
                }
              />

              <ValidatorForm id="valuationForm" onSubmit={handleSearch}>
                <div className={styles.validatorForm}>
                  <div className={styles.textBox}>
                    <TextValidator
                      placeholder={salesCodePlaceholder}
                      name="searchBySalesCodePA"
                      autoComplete="off"
                      inputProps={{
                        "data-testid": "dealerSearchTextInput",
                      }}
                      onChange={handleInputChange}
                      onFocus={() =>
                        setStateFields({
                          searchMethod: "searchBySalesCodePA",
                          orderId: "",
                        })
                      }
                      focused={state.searchMethod === "searchBySalesCodePA"}
                      value={state.code}
                    />
                  </div>
                  <SearchButton
                    testId="dealerSearchButtonBySalesCode"
                    disabled={state.code === ""}
                    {...props}
                  />
                </div>
              </ValidatorForm>
            </Grid>
            <Grid item>
              <div className={styles.labelPadding}>
                {props.t("CrcOrderSearch.or")}
              </div>
            </Grid>
            <Grid item>
              <FormControlLabel
                value="searchByOrderId"
                control={<Radio />}
                data-testid="searchByOrderId"
                label={
                  <Typography variant="body2">
                    {props.t("CrcOrderSearch.searchByOrderId")}
                  </Typography>
                }
              />

              {IS_CANADA_MARKET(props.user.market) && (
                <div style={{ display: "inline-flex", fontSize: "0px" }}>
                  <SharedToolTip
                    className={styles.iBallOffset}
                    placement="top-start"
                    {...props}
                    openTestid="recordSearchToolTipOpen"
                    body={props.t("CrcOrderSearch.recordSearchToolTipBody")}
                  />
                </div>
              )}

              <ValidatorForm id="valuationForm" onSubmit={handleSearch}>
                <div className={styles.validatorForm}>
                  <div className={styles.textBox}>
                    <TextValidator
                      placeholder={props.t(
                        "CrcOrderSearch.textInputPlaceholder"
                      )}
                      name="searchByOrderId"
                      autoComplete="off"
                      id="orderSearch"
                      onChange={handleInputChange}
                      value={state.orderId}
                      focused={state.searchMethod === "searchByOrderId"}
                      onFocus={() =>
                        setStateFields({
                          searchMethod: "searchByOrderId",
                          code: "",
                        })
                      }
                    />
                  </div>

                  <SearchButton
                    testId="dealerSearchButtonByOrderId"
                    disabled={state.orderId === ""}
                    {...props}
                  />
                </div>
              </ValidatorForm>
            </Grid>
          </Grid>
        </RadioGroup>

        {salesCodeResultContent}
        {orderSearchResultContent}
      </div>
    </section>
  );
};

const SearchButton = (props) => {
  return (
    <Box ml={1}>
      <EMPButton
        testId={props.testId}
        submitButton
        variant="contained"
        disabled={props.disabled}
      >
        {props.t("fieldView.search")}
      </EMPButton>
    </Box>
  );
};

DealerSearch.propTypes = {
  t: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    language: PropTypes.string,
  }).isRequired,
  brand: PropTypes.string,
  hideOrShow: PropTypes.func.isRequired,
  updateCurrentDealer: PropTypes.func.isRequired,
  user: PropTypes.shape({
    market: PropTypes.string.isRequired,
    brand: PropTypes.string,
    dealerBrand: PropTypes.string,
    userType: PropTypes.string,
    setDealerGroup: PropTypes.func.isRequired,
    setLanguageCode: PropTypes.func.isRequired,
    updateBrand: PropTypes.func.isRequired,
  }).isRequired,
};

SearchButton.propTypes = {
  testId: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation("emp")(DealerSearch);
